import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckIcon } from '@heroicons/react/24/outline';

const PRO_FEATURES = [
  'Unlimited Visions',
  'AI advice tailored to your real numbers',
  '3-month Vision strategies with monthly milestones',
  'Connect bank accounts via Plaid',
  'Share your Vision progress cards',
  'Full Ask Aura and Co-Pilot guidance',
];

const PRO_PLUS_FEATURES = [
  'Everything in Aura Pro',
  '1 free verified advisor consultation per month',
  'Priority AI responses',
  'Advisor matching to your Vision',
];

const Upgrade = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTier = searchParams.get('tier') === 'pro_plus' ? 'pro_plus' : 'pro';
  const [billing, setBilling] = useState('monthly');
  const [selectedTier, setSelectedTier] = useState(initialTier);
  const [email, setEmail] = useState(user?.email || '');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleWaitlist = async (tier) => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/upgrade/waitlist', { email, tier });
      setSubmitted(true);
      setSelectedTier(tier);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not join waitlist. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-body text-ink-muted hover:text-ink mb-8"
        >
          ← Back
        </button>

        <h1 className="font-display text-display text-ink mb-8 text-center">Choose your plan</h1>

        <div className="flex justify-center gap-2 mb-10">
          <button
            type="button"
            onClick={() => setBilling('monthly')}
            className={`px-4 py-2 rounded-md text-body font-medium border transition-colors ${
              billing === 'monthly' ? 'bg-pine text-bone-raised border-pine' : 'border-hairline text-ink-muted'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBilling('annual')}
            className={`px-4 py-2 rounded-md text-body font-medium border transition-colors flex items-center gap-2 ${
              billing === 'annual' ? 'bg-pine text-bone-raised border-pine' : 'border-hairline text-ink-muted'
            }`}
          >
            Annual
            <span className="text-[10px] uppercase tracking-wide bg-bone-raised text-pine px-1.5 py-0.5 rounded">
              Best value — Save 34%
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div
            className={`rounded-xl border p-6 transition-colors ${
              selectedTier === 'pro' ? 'border-pine bg-pine-tint' : 'border-hairline bg-bone-raised'
            }`}
          >
            <h2 className="font-display text-h2 text-ink mb-1">Aura Pro</h2>
            <p className="text-h3 font-medium text-ink mb-4">
              {billing === 'annual' ? (
                <>
                  $79/year <span className="text-body text-ink-muted font-normal">($6.58/month)</span>
                </>
              ) : (
                '$9.99/month'
              )}
            </p>
            <ul className="space-y-2 mb-6">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-body text-ink">
                  <CheckIcon className="w-4 h-4 text-pine shrink-0 mt-1" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                setSelectedTier('pro');
                handleWaitlist('pro');
              }}
              disabled={loading}
              className="btn-primary w-full"
            >
              {submitted && selectedTier === 'pro'
                ? 'On the waitlist'
                : 'Payment coming soon — join the waitlist'}
            </button>
          </div>

          <div
            className={`rounded-xl border p-6 transition-colors ${
              selectedTier === 'pro_plus' ? 'border-ink bg-bone-raised' : 'border-hairline bg-bone-raised'
            }`}
          >
            <h2 className="font-display text-h2 text-ink mb-1">Aura Pro+</h2>
            <p className="text-h3 font-medium text-ink mb-1">$19.99/month</p>
            <p className="text-label text-ink-muted mb-4">Annual billing not available in V1</p>
            <ul className="space-y-2 mb-6">
              {PRO_PLUS_FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-body text-ink">
                  <CheckIcon className="w-4 h-4 text-pine shrink-0 mt-1" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                setSelectedTier('pro_plus');
                handleWaitlist('pro_plus');
              }}
              disabled={loading}
              className="w-full py-3 rounded-md bg-ink text-bone-raised font-medium text-body hover:opacity-95 disabled:opacity-50"
            >
              {submitted && selectedTier === 'pro_plus'
                ? 'On the waitlist'
                : 'Payment coming soon — join the waitlist'}
            </button>
          </div>
        </div>

        {submitted ? (
          <p className="text-center text-body text-pine font-medium mb-4">
            Payment coming soon — you&apos;re on the waitlist. We&apos;ll email you when checkout is live.
          </p>
        ) : (
          <p className="text-center text-body text-ink-muted mb-4">
            Payment coming soon — join the waitlist with your email below.
          </p>
        )}

        <div className="max-w-md mx-auto mb-6">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full px-4 py-3 rounded-md border border-hairline bg-bone-raised text-body text-ink"
          />
          {error && <p className="text-body text-warning mt-2">{error}</p>}
        </div>

        <p className="text-center text-label text-ink-muted">All plans include a 7-day free trial</p>
        <p className="text-center text-label text-ink-muted mt-1">Cancel anytime. No hidden fees.</p>
      </div>
    </div>
  );
};

export default Upgrade;
